/**
 * 화면 1 · 대기 등록 (FormScreen) — 디자인 원본 Pantalla 1.
 *
 * @see #781 / admin#2111 설계 §6.1
 */
import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, Clock, MessageCircle, ShoppingBag, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MenuQuantityControl } from '@/components/menu/MenuQuantityControl';
import type { Locale } from '@/config/locales';
import type {
  WaitlistMarketingConsent,
  WaitlistMarketingConsentAvailable,
  WaitlistQueueType,
  WaitlistStatusData,
} from '@/lib/api/waitlist';
import { WaitlistAppBar, WaitlistSegments, WaitlistWarning } from './WaitlistParts';
import { WaitlistMenuButton } from './WaitlistMenuButton';
import {
  DEFAULT_DIAL_CODE,
  WAITLIST_COUNTRIES,
  findWaitlistCountry,
  formatWaitlistPhone,
  isValidWaitlistPhone,
  toPhoneDigits,
  type WaitlistStrings,
} from './strings';

export interface WaitlistJoinInput {
  mode: WaitlistQueueType;
  /**
   * 픽업 등록은 인원 선택 UI 자체가 없다 — 서버가 1로 고정하므로(#794) 아예 보내지
   * 않는다. 테이블 등록만 값을 채운다.
   */
  partySize?: number;
  /** 구분자 없는 숫자열 (선행 0 제거). */
  phoneDigits: string;
  dialCode: string;
  /** 사용자가 현재 서버 문구를 명시적으로 선택한 경우에만 존재한다. */
  marketingConsent?: {
    version: string;
    hash: string;
    locale: string;
  };
}

interface WaitlistFormScreenProps {
  t: WaitlistStrings;
  storeName: string;
  storeZone: string;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  /** 등록할 큐 종류(#794) — 인원 스테퍼 노출 여부와 제출 payload 를 분기한다. */
  mode: WaitlistQueueType;
  /** 선택 화면(ChooseScreen)으로 돌아갈 수 있는지 — 단일 큐만 열린 매장은 false. */
  canGoBack: boolean;
  onBack: () => void;
  /** 매장 설정 상한 (waitlist-status.max_party_size). 픽업 모드에서는 쓰지 않는다. */
  maxPartySize: number;
  waitingCount: number;
  estMinutes: number;
  submitting: boolean;
  /** 등록 실패 안내 (중복/닫힘 등). null 이면 미표시. */
  errorMessage: string | null;
  onJoin: (input: WaitlistJoinInput) => void;
  /** 있으면 "메뉴 보기" 버튼을 노출한다(#794 통합). */
  storeMeta?: WaitlistStatusData['store_meta'];
  /** Store/revision gate가 활성인 경우에만 서버가 제공하는 선택형 동의 문구. */
  marketingConsent?: WaitlistMarketingConsent;
  /** Store 전환·새 등록 복귀마다 이전 선택 상태를 폐기하기 위한 세대 키. */
  registrationKey: string;
}

/**
 * 스테퍼 상한 — 디자인 원본과 동일하게 매장 상한(`maxPartySize`)보다 높게 두어
 * 초과 입력 자체는 허용하고 경고로 안내한다. 서버도 20 을 절대 상한으로 검증한다.
 *
 * @see olulo-mx-admin JoinWaitlistRequest::ABSOLUTE_MAX_PARTY_SIZE
 */
const PARTY_STEPPER_MAX = 20;

/** 펼친 전문 컨테이너 id — 화면에 동의 블록이 하나뿐이라 상수로 고정한다. */
const MARKETING_CONSENT_BODY_ID = 'waitlist-marketing-consent-body';

/** 동의 체크박스 id — 확대된 탭 타깃 라벨(`htmlFor`)과 짝을 이룬다. */
const MARKETING_CONSENT_CHECKBOX_ID = 'waitlist-marketing-consent-checkbox';

/**
 * 전문 평문 안의 http(s) URL 을 찾는다.
 *
 * 서버는 마크업 없는 평문을 내려주고(해시 대상이므로) URL 도 그대로 박혀 있다
 * — 예: `Consulta nuestro Aviso de privacidad. [https://…/aviso-privacidad.pdf]`.
 * 공백 전까지 삼킨 뒤 문장 부호/괄호만 되돌려 링크 밖으로 뺀다.
 */
const CONSENT_URL_PATTERN = /https?:\/\/\S+/g;
const CONSENT_URL_TRAILING = /[.,;:!?)\]}>"'»…]+$/;

/** 전문 한 줄을 텍스트/링크 조각으로 쪼갠다. */
function splitConsentLine(line: string): Array<{ text: string; href?: string }> {
  const parts: Array<{ text: string; href?: string }> = [];
  let cursor = 0;

  for (const match of line.matchAll(CONSENT_URL_PATTERN)) {
    const start = match.index ?? 0;
    const url = match[0].replace(CONSENT_URL_TRAILING, '');
    // 문장 부호만 남은 잔여물은 링크가 아니라 일반 텍스트로 되돌린다.
    if (url.length === 0) continue;

    if (start > cursor) parts.push({ text: line.slice(cursor, start) });
    parts.push({ text: url, href: url });
    cursor = start + url.length;
  }

  if (cursor < line.length) parts.push({ text: line.slice(cursor) });

  return parts;
}

/**
 * 동의 전문(평문)을 단락/줄바꿈/링크로 렌더한다.
 *
 * 계약: 단락 구분 `\n\n`, 줄바꿈 `\n`, **첫 단락은 제목**. 서버 텍스트는 해시
 * 대상이라 마크업이 없으므로(설계 §A) 굵게 등 구간 강조는 재현하지 않는다 —
 * 유일한 예외가 계약으로 보장된 제목 단락이고, 이것만 CSS 로 강조한다.
 *
 * 접힘은 언마운트가 아니라 `hidden` 으로 표현한다 — 토글의 `aria-controls` 가
 * 가리키는 대상이 DOM 에 항상 있어야 보조기술이 "제어 대상으로 이동"할 수 있고,
 * 체크박스의 `aria-describedby` 도 상태와 무관하게 전문을 가리킬 수 있다.
 */
function WaitlistMarketingConsentBody({ text, hidden }: { text: string; hidden: boolean }) {
  const paragraphs = useMemo(
    () =>
      // 서버가 CRLF 로 저장된 문구를 내려주면 `\n\n` 분리가 통째로 실패한다 — 먼저 정규화한다.
      text
        .replace(/\r\n?/g, '\n')
        .split('\n\n')
        .map((paragraph) => paragraph.split('\n'))
        .filter((lines) => lines.some((line) => line.trim().length > 0)),
    [text],
  );
  /**
   * 제목 강조는 "제목 + 본문" 구조일 때만 의미가 있다. 단락이 하나뿐인 문구
   * (제목 없는 locale 등)까지 index 0 만 보고 강조하면 전문 전체가 제목이 된다.
   */
  const hasTitleParagraph = paragraphs.length > 1;

  return (
    <div
      id={MARKETING_CONSENT_BODY_ID}
      data-testid="waitlist-marketing-consent-body"
      hidden={hidden}
      className="mt-2.5 flex flex-col gap-2 text-[13px] leading-snug text-muted-foreground"
    >
      {paragraphs.map((lines, paragraphIndex) => (
        <p
          key={paragraphIndex}
          className={
            hasTitleParagraph && paragraphIndex === 0 ? 'm-0 font-medium text-foreground' : 'm-0'
          }
        >
          {lines.map((line, lineIndex) => (
            <Fragment key={lineIndex}>
              {lineIndex > 0 && <br />}
              {splitConsentLine(line).map((part, partIndex) =>
                part.href ? (
                  <a
                    key={partIndex}
                    href={part.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all underline underline-offset-2"
                  >
                    {part.text}
                  </a>
                ) : (
                  <Fragment key={partIndex}>{part.text}</Fragment>
                ),
              )}
            </Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}

/**
 * 선택형 마케팅 동의 — 접힌 요약 라벨 + 펼침 전문(#882).
 *
 * 체크박스와 펼침 토글은 **별개의 컨트롤**이다. 라벨 텍스트를 `<label for>` 로
 * 묶으면 전문을 읽으려는 탭이 곧바로 동의 체크가 되어버린다 — 동의는 명시적
 * affirmative click 이어야 하므로 요약 텍스트·chevron 은 `<button>` 으로 두고
 * 체크박스에는 같은 요약을 `aria-label` 로 준다(접근성 이름 유지).
 *
 * 대신 체크박스가 16px 컨트롤 하나로 남지 않도록 **텍스트 없는 라벨**로 감싸
 * 탭 타깃만 40px 로 넓힌다(WCAG 2.5.8). 요약 텍스트는 여전히 토글 소유다.
 *
 * v1 서버(summary 없음)는 전문을 라벨로 붙이던 기존 렌더로 폴백한다.
 */
function WaitlistMarketingConsentField({
  consent,
  checked,
  onCheckedChange,
  detailsLabel,
}: {
  consent: WaitlistMarketingConsentAvailable;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** 펼침 토글에만 붙는 보조기술 전용 문구 — 체크박스와 접근성 이름을 구분한다. */
  detailsLabel: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const summary = consent.summary;

  if (!summary) {
    return (
      <div
        data-testid="waitlist-marketing-consent"
        className="mb-5 flex items-start gap-2.5 rounded-lg border border-border bg-background px-3.5 py-3"
      >
        <Checkbox
          id={MARKETING_CONSENT_CHECKBOX_ID}
          checked={checked}
          onCheckedChange={(next) => onCheckedChange(next === true)}
        />
        <Label
          htmlFor={MARKETING_CONSENT_CHECKBOX_ID}
          className="items-start text-[13px] font-normal leading-snug text-muted-foreground"
        >
          {consent.text}
        </Label>
      </div>
    );
  }

  return (
    <div
      data-testid="waitlist-marketing-consent"
      className="mb-5 rounded-lg border border-border bg-background px-3.5 py-3"
    >
      {/* gap 은 라벨 패딩(12px)과 같은 값이라 확대된 체크박스 타깃이 토글을 침범하지 않는다. */}
      <div className="flex items-start gap-3">
        {/*
          요약 텍스트를 넣지 않는 빈 라벨 — 음수 마진으로 레이아웃은 그대로 두고
          히트 영역만 40px 로 넓힌다. 텍스트를 넣으면 전문을 읽으려는 탭이 동의
          체크가 되므로(#882 원칙) 라벨은 체크박스만 감싼다.
        */}
        <label
          htmlFor={MARKETING_CONSENT_CHECKBOX_ID}
          data-testid="waitlist-marketing-consent-checkbox-hit"
          className="-m-3 flex shrink-0 cursor-pointer p-3"
        >
          <Checkbox
            id={MARKETING_CONSENT_CHECKBOX_ID}
            aria-label={summary}
            /* 동의는 요약이 아니라 요약+전문에 대한 것이다(증거 rendered_text_hash). */
            aria-describedby={MARKETING_CONSENT_BODY_ID}
            className="mt-0.5"
            checked={checked}
            onCheckedChange={(next) => onCheckedChange(next === true)}
          />
        </label>
        <button
          type="button"
          data-testid="waitlist-marketing-consent-toggle"
          aria-expanded={expanded}
          aria-controls={MARKETING_CONSENT_BODY_ID}
          onClick={() => setExpanded((previous) => !previous)}
          className="flex min-w-0 flex-1 items-start gap-1.5 bg-transparent p-0 text-start text-[13px] font-normal leading-snug text-muted-foreground"
        >
          {/* 화면 장식일 뿐 해시 대상 문구가 아니다(오너 결정 2026-09-12). */}
          <span aria-hidden="true" className="shrink-0 leading-snug">
            🎁
          </span>
          <span className="min-w-0 flex-1">{summary}</span>
          {/* 체크박스와 이름이 같으면 보조기술 순회에서 두 컨트롤을 구분할 수 없다. */}
          <span className="sr-only">{detailsLabel}</span>
          <ChevronDown
            aria-hidden="true"
            className={`mt-px size-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      <WaitlistMarketingConsentBody text={consent.text} hidden={!expanded} />
    </div>
  );
}

export function WaitlistFormScreen({
  t,
  storeName,
  storeZone,
  locale,
  onLocaleChange,
  mode,
  canGoBack,
  onBack,
  maxPartySize,
  waitingCount,
  estMinutes,
  submitting,
  errorMessage,
  onJoin,
  storeMeta,
  marketingConsent,
  registrationKey,
}: WaitlistFormScreenProps) {
  const pk = mode === 'pickup';
  const [partySize, setPartySize] = useState(2);
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const [phone, setPhone] = useState('');
  const activeMarketingConsent =
    marketingConsent?.available === true && marketingConsent.status === 'available'
      ? marketingConsent
      : null;
  const currentMarketingConsentIdentity = activeMarketingConsent
    ? JSON.stringify([
        registrationKey,
        mode,
        activeMarketingConsent.version,
        activeMarketingConsent.hash,
        activeMarketingConsent.locale,
      ])
    : null;
  const [selectedMarketingConsentIdentity, setSelectedMarketingConsentIdentity] = useState<
    string | null
  >(null);
  const marketingConsentChecked =
    currentMarketingConsentIdentity !== null &&
    selectedMarketingConsentIdentity === currentMarketingConsentIdentity;

  const country = useMemo(() => findWaitlistCountry(dialCode), [dialCode]);
  const phoneOk = isValidWaitlistPhone(phone, country);
  // 픽업은 인원 스테퍼가 없으므로 상한 초과 자체가 발생하지 않는다.
  const overMax = !pk && partySize > maxPartySize;
  const canSubmit = phoneOk && !overMax && !submitting;
  const showPhoneError = phone.length > 0 && !phoneOk;

  return (
    <div
      data-testid="waitlist-form-page"
      className="flex h-screen flex-col overflow-hidden supports-[height:100dvh]:h-dvh"
    >
      <WaitlistAppBar
        storeName={storeName}
        subtitle={storeZone}
        locale={locale}
        onLocaleChange={onLocaleChange}
        languageLabel={t.idioma}
        logoUrl={storeMeta?.logo_url}
        mapsUrl={storeMeta?.maps_url}
      />

      <div
        data-testid="waitlist-form-scroll-region"
        className="min-h-0 flex-1 overflow-y-auto"
      >
        <div data-testid="waitlist-form-content" className="px-5 pb-5 pt-1">
          {canGoBack && (
            <button
              type="button"
              onClick={onBack}
              className="mb-2 flex w-fit items-center gap-1 bg-transparent p-0 text-[13px] font-semibold text-muted-foreground"
            >
              {/* RTL(아랍어 등)에서는 "뒤로"가 시각적으로 오른쪽을 가리켜야 한다 —
                  아이콘만 좌우 반전한다(#794 리뷰). */}
              <ChevronLeft className="size-4 rtl:scale-x-[-1]" />
              {pk ? t.modePickup : t.modeTable}
            </button>
          )}
          <Badge variant="secondary" className="mb-3">
            {t.filaAbierta}
          </Badge>
          <h1 className="m-0 mb-1 text-[26px] font-extrabold leading-tight tracking-[-0.02em]">
            {pk ? t.pkJoinTitle : t.joinTitle}
          </h1>
          <p className="m-0 mb-5 text-sm leading-relaxed text-muted-foreground">
            {pk ? t.pkJoinLead : t.joinLead}
          </p>

          {pk ? (
            <div className="mb-4 flex items-start gap-2.5 rounded-lg bg-background-secondary px-3.5 py-3">
              <span className="mt-px shrink-0 text-secondary">
                <ShoppingBag className="size-[18px]" />
              </span>
              <div className="text-[13px] leading-snug text-muted-foreground">{t.pkRefHelp}</div>
            </div>
          ) : (
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Users className="size-[17px]" />
                  <span className="text-sm font-semibold">{t.howMany}</span>
                </div>
                <MenuQuantityControl
                  quantity={partySize}
                  minQuantity={1}
                  maxQuantity={PARTY_STEPPER_MAX}
                  onQuantityChange={setPartySize}
                  label={t.personas}
                />
                {overMax && (
                  <div className="mt-3">
                    <WaitlistWarning>{t.warnMax(maxPartySize)}</WaitlistWarning>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="mb-5 flex flex-col gap-1.5">
            <Label htmlFor="waitlist-tel">{t.telefono}</Label>
            {/* dir=ltr 고정 — 전화번호와 국가코드는 RTL 로케일에서도 좌→우 표기다. */}
            <div className="flex gap-2" dir="ltr">
              <select
                aria-label={t.telefono}
                value={dialCode}
                onChange={(event) => {
                  setDialCode(event.target.value);
                  // 국가가 바뀌면 이전 국가 포맷의 잔여 입력이 새 자리수 규칙과 어긋난다.
                  setPhone('');
                }}
                className="h-9 w-[122px] shrink-0 rounded-md border border-border bg-input-background pl-3 pr-7 text-sm font-semibold text-foreground"
              >
                {WAITLIST_COUNTRIES.map((option) => (
                  <option key={option.dial + option.name} value={option.dial}>
                    {option.dial} {option.name}
                  </option>
                ))}
              </select>
              <Input
                id="waitlist-tel"
                type="tel"
                inputMode="numeric"
                placeholder={t.telPh}
                className="flex-1"
                value={phone}
                onChange={(event) => setPhone(formatWaitlistPhone(event.target.value, dialCode))}
                aria-invalid={showPhoneError ? true : undefined}
              />
            </div>
            <span
              className={`text-xs ${showPhoneError ? 'text-destructive-dark' : 'text-muted-foreground'}`}
            >
              {showPhoneError ? t.telError : t.telHelp}
            </span>
          </div>

          {activeMarketingConsent && (
            <WaitlistMarketingConsentField
              /* notice 가 바뀌면 펼침 상태까지 새 문구 기준으로 초기화한다. */
              key={currentMarketingConsentIdentity ?? ''}
              consent={activeMarketingConsent}
              detailsLabel={t.mcShowDetails}
              checked={marketingConsentChecked}
              onCheckedChange={(checked) =>
                setSelectedMarketingConsentIdentity(
                  checked ? currentMarketingConsentIdentity : null,
                )
              }
            />
          )}

          <div
            data-testid="waitlist-operational-notification"
            className="mb-5 flex items-start gap-2.5 rounded-lg bg-background-secondary px-3.5 py-3"
          >
            <span className="mt-px shrink-0 text-primary">
              <MessageCircle className="size-[18px]" />
            </span>
            <div className="text-[13px] leading-snug text-muted-foreground">{t.notifyNote}</div>
          </div>

          <div className="flex items-center gap-2.5 rounded-lg bg-background-secondary px-3.5 py-3">
            <Clock className="size-[18px] shrink-0" />
            <div className="text-[13px] leading-snug">
              <WaitlistSegments segments={t.teamsInfo(waitingCount, estMinutes)} />
            </div>
          </div>

          {storeMeta && (
            <div className="mt-4">
              <WaitlistMenuButton
                t={t}
                menuViewUrl={storeMeta.menu_view_url}
                menuViewKind={storeMeta.menu_view_kind}
                menuUrl={storeMeta.menu_urls?.digital}
                menuPdfUrl={storeMeta.menu_urls?.pdf}
              />
            </div>
          )}

          {errorMessage && (
            <div className="mt-4">
              <WaitlistWarning>{errorMessage}</WaitlistWarning>
            </div>
          )}
        </div>
      </div>

      <div
        data-testid="waitlist-form-action-bar"
        className="shrink-0 border-t border-border bg-background px-5 pb-[calc(20px+env(safe-area-inset-bottom))] pt-3"
      >
        <Button
          size="lg"
          className="w-full"
          disabled={!canSubmit}
          onClick={() =>
            onJoin({
              mode,
              phoneDigits: toPhoneDigits(phone),
              dialCode,
              ...(pk ? {} : { partySize }),
              ...(activeMarketingConsent && marketingConsentChecked
                ? {
                    marketingConsent: {
                      version: activeMarketingConsent.version,
                      hash: activeMarketingConsent.hash,
                      locale: activeMarketingConsent.locale,
                    },
                  }
                : {}),
            })
          }
        >
          {submitting ? t.joining : pk ? t.pkJoinTitle : t.joinCta}
        </Button>
      </div>
    </div>
  );
}
